import classNames from "classnames";
import AscButton from "../AscButton/AscButton";
import AscModal from "../AscModal/AscModal";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscParagraph from "../AscTypography/AscParagraph/AscParagraph";
import styles from "./AscModuleSurveyModal.module.scss";
import BackgroundDecoration from "../../assets/illustrations/module-survey-background-decoration.svg";
import FinishedImagePlaceholder from "../../assets/images/module-image-1.webp";
import { CloseFilledIcon } from "../AscIcons";
import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import AscModuleSurveyRadioGroup from "./AscModuleSurveyRadioGroup/AscModuleSurveyRadioGroup";
import AscImage from "../AscImage/AscImage";
import { AscImageSource, AscModuleSurveyQuestion } from "@ascendnow/shared";

export interface AscModuleSurveyModalProps {
  className?: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  surveyTitle: string;
  surveyDescription: string;
  closable?: boolean;
  questions: AscModuleSurveyQuestion[];
  finishedDescription?: string;
  finishedTitle?: string;
  finishImageUrl?: AscImageSource;
  onSubmit: (
    answers: Record<"questionId" | "response", string>[]
  ) => Promise<boolean>;
  onFinish?: () => void;
  loading?: boolean;
}

export default function AscModuleSurveyModal({
  className,
  visible,
  setVisible,
  surveyTitle,
  surveyDescription,
  finishedDescription = "Thanks for completing the form",
  finishedTitle = "You're all set!",
  closable = true,
  questions,
  onSubmit,
  loading,
  finishImageUrl = FinishedImagePlaceholder,
  onFinish,
}: AscModuleSurveyModalProps) {
  const [step, setStep] = useState(0);

  const formik = useFormik<Record<string, string>>({
    initialValues: questions?.reduce(
      (acc, question) => ({
        ...acc,
        [question.id]: "",
      }),
      {}
    ),
    validationSchema: Yup.object(
      questions?.reduce(
        (acc, question) => ({
          ...acc,
          [question.id]: Yup.string().required("This question is required."),
        }),
        {}
      )
    ),
    onSubmit: async (values) => {
      const answers = Object.entries(values).map(([questionId, response]) => ({
        questionId,
        response: response as string,
      }));
      const isSubmitted = await onSubmit(answers);
      if (isSubmitted) {
        setStep(2);
      }
    },
  });

  const handleGetStarted = useCallback(() => {
    if (step === 0) {
      setStep(1);
    }
  }, [step]);

  function handleFinish() {
    setVisible(false);
    onFinish?.();
  }

  const content = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <>
            <AscHeading className={styles.surveyTitle}>
              {surveyTitle}
            </AscHeading>
            <AscParagraph className={styles.surveyDescription}>
              {surveyDescription}
            </AscParagraph>
            <AscButton
              className={styles.actionButton}
              onClick={handleGetStarted}
            >
              Get Started
            </AscButton>
          </>
        );
      case 1:
        return (
          <>
            <AscHeading className={styles.surveyTitle}>
              {surveyTitle}
            </AscHeading>

            <form
              onSubmit={formik?.handleSubmit}
              className={classNames(styles.form)}
            >
              {questions?.map((question, index) => (
                <div key={question?.id} className={styles.fieldGroup}>
                  <div className={styles.fieldLabel}>
                    {index + 1}
                    {". "}
                    {question?.question}
                  </div>
                  {question?.description ? (
                    <div className={styles.fieldDescription}>
                      {question?.description}
                    </div>
                  ) : null}
                  <AscModuleSurveyRadioGroup
                    className={styles.radioGroup}
                    name={question?.id}
                    type={question?.type}
                    onChange={formik?.handleChange}
                    onBlur={formik?.handleBlur}
                    value={formik?.values[question?.id]}
                    error={formik?.errors[question?.id] as string}
                    touched={formik?.touched[question?.id] as boolean}
                  />
                </div>
              ))}
              <AscButton
                type="submit"
                loading={loading}
                className={styles.actionButton}
              >
                Submit
              </AscButton>
            </form>
          </>
        );
      case 2:
        return (
          <>
            <AscImage className={styles.finishImage} src={finishImageUrl} />
            <AscHeading className={styles.finishedTitle}>
              {finishedTitle}
            </AscHeading>
            <AscParagraph className={styles.surveyDescription}>
              {finishedDescription}
            </AscParagraph>
            <AscButton className={styles.actionButton} onClick={handleFinish}>
              Exit
            </AscButton>
          </>
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    surveyTitle,
    surveyDescription,
    handleGetStarted,
    formik?.handleSubmit,
    formik?.handleChange,
    formik?.handleBlur,
    formik?.values,
    formik?.errors,
    formik?.touched,
    questions,
    loading,
    finishImageUrl,
    finishedTitle,
    finishedDescription,
  ]);

  function handleSetVisible(visible: boolean) {
    if (!closable && !visible) {
      return;
    }
    setVisible(visible);
  }

  function handleClose() {
    handleSetVisible(false);
  }

  return (
    <AscModal
      hasCloseBtn={false}
      visible={visible}
      setVisible={handleSetVisible}
      modalClassName={classNames(
        styles.ground,
        {
          [styles.secondStep]: step === 1,
        },
        className
      )}
      innerContentClassName={classNames(styles.content)}
      backgroundDecorationUrl={BackgroundDecoration}
      backdropClassName={styles.backdrop}
    >
      {closable ? (
        <CloseFilledIcon onClick={handleClose} className={styles.closeIcon} />
      ) : null}

      {content}
    </AscModal>
  );
}
